import { z } from 'zod';

/**
 * Reviewer feedback on an execution result (ADR-031). One decision per reviewed element;
 * accepted or corrected content becomes a knowledge CANDIDATE (HUMAN provenance) that a
 * knowledge reviewer validates before recall uses it.
 */
export const FeedbackItem = z.object({
  /** Location of the element in the execution output, e.g. "ifs.gbaPlan.12". */
  path: z.string().regex(/^[A-Za-z0-9_.-]{1,200}$/),
  /** Human-readable name of the element (becomes the candidate's title). */
  label: z.string().min(1).max(300),
  decision: z.enum(['accept', 'edit', 'reject']),
  /** Accepted or corrected value (required for accept/edit). */
  value: z.string().max(4000).optional(),
  note: z.string().max(1000).optional(),
  /** Knowledge type of the candidate (a registered type, e.g. Fact, LessonLearned). */
  type: z.string().min(1).max(100).default('Fact'),
});
export type FeedbackItem = z.infer<typeof FeedbackItem>;

export const FeedbackInput = z.object({ items: z.array(FeedbackItem).min(1).max(100) });
export type FeedbackInput = z.infer<typeof FeedbackInput>;

export const FeedbackView = z.object({
  path: z.string(),
  label: z.string(),
  decision: z.enum(['accept', 'edit', 'reject']),
  value: z.string().nullable(),
  note: z.string().nullable(),
  knowledgeItemId: z.string().uuid().nullable(),
  /** Status of the resulting knowledge item (CANDIDATE until validated). */
  knowledgeStatus: z.string().nullable(),
  reviewedBy: z.object({ userId: z.string().uuid(), email: z.string().nullable() }),
  createdAt: z.iso.datetime(),
});
export type FeedbackView = z.infer<typeof FeedbackView>;

export const FeedbackResult = z.object({
  recorded: z.number().int(),
  /** Paths already reviewed (a decision is recorded once). */
  alreadyReviewed: z.array(z.string()),
  candidates: z.array(z.object({ path: z.string(), knowledgeItemId: z.string().uuid() })),
});
export type FeedbackResult = z.infer<typeof FeedbackResult>;
